import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

import paths from '@src/paths';
import { toast } from '@src/components/core/toaster';
import { quickCreateClient } from '@src/api/actions/clients';
import { fetchProspect, fetchProspectVerticals } from '@src/api/actions/prospects';
import { fetchVerticals } from '@src/api/actions/verticals';

function parseZips(text) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const hasTab = line.includes('\t');
      const hasPipe = line.includes('|');
      let parts;
      if (hasTab) parts = line.split('\t').map((p) => p.trim());
      else if (hasPipe) parts = line.split('|').map((p) => p.trim());
      else parts = line.split(',').map((p) => p.trim());
      let zip = parts[0] || '';
      if (/^\d{4}$/.test(zip)) zip = `0${zip}`;
      return {
        zip,
        territory: parts[1] || '',
        vertical: parts[2] || '',
        market_set: (parts[3] || '').trim(),
      };
    })
    .filter((r) => r.zip);
}

const fmtCurrency = (n) => {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const SPEND_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'guru_funds_spend', label: 'Lead Gurus funds ad spend' },
  { value: 'client_funds_spend', label: 'Client funds ad spend' },
];

export default function ClientCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prospectToken = searchParams.get('prospect') || '';

  const [name, setName] = useState('');
  const [availableVerticals, setAvailableVerticals] = useState([]);
  const [selectedVerticals, setSelectedVerticals] = useState([]);
  const [verticalBudgets, setVerticalBudgets] = useState({});
  const [zipText, setZipText] = useState('');
  const [spendFunding, setSpendFunding] = useState('');
  const [launchDate, setLaunchDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingProspect, setLoadingProspect] = useState(false);

  // Load verticals
  useEffect(() => {
    fetchVerticals()
      .then((res) => {
        const data = res?.data;
        const list = Array.isArray(data) ? data.map((v) => v.vertical || v.name || v) : [];
        setAvailableVerticals(list);
      })
      .catch(() => {
        // Fallback to public endpoint
        fetchProspectVerticals()
          .then((res) => setAvailableVerticals(res?.data?.verticals || []))
          .catch(() => {});
      });
  }, []);

  // Pre-fill from prospect
  useEffect(() => {
    if (!prospectToken) return;
    setLoadingProspect(true);
    fetchProspect(prospectToken)
      .then((res) => {
        const p = res?.data;
        if (!p) return;
        setName(p.company_name || '');
        setSelectedVerticals(p.verticals || []);
        setVerticalBudgets(p.vertical_budgets || {});
        if (p.zip_data?.length) {
          setZipText(p.zip_data.map((r) => {
            const parts = [r.zip, r.territory, r.vertical].filter(Boolean);
            return parts.join(' | ');
          }).join('\n'));
        }
      })
      .catch(() => toast.error('Failed to load prospect data'))
      .finally(() => setLoadingProspect(false));
  }, [prospectToken]);

  const zipRows = useMemo(() => parseZips(zipText), [zipText]);

  const totalBudget = useMemo(
    () => selectedVerticals.reduce((sum, v) => sum + (Number(verticalBudgets[v]) || 0), 0),
    [verticalBudgets, selectedVerticals]
  );

  const handleToggleVertical = (v) => {
    setSelectedVerticals((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    );
    if (!verticalBudgets[v]) {
      setVerticalBudgets((prev) => ({ ...prev, [v]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Company name is required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await quickCreateClient({
        name: name.trim(),
        verticals: selectedVerticals,
        zip_rows: zipRows,
        budget: totalBudget,
        vertical_budgets: verticalBudgets,
        spend_funding: spendFunding,
        desired_launch_date: launchDate || null,
        prospect_token: prospectToken,
      });
      const clientSlug = res?.data?.client_slug;
      toast.success(`Client "${res?.data?.client_name}" created!`);
      navigate(paths.clientHub(clientSlug));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.name?.[0] || 'Failed to create client';
      toast.error(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingProspect) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">Loading prospect data...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>New Client</Typography>
          <Typography variant="body2" color="text.secondary">
            Create a new client with territories, budgets, and projections in one step.
          </Typography>
        </Box>
      </Stack>

      {prospectToken && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Pre-filled from prospect submission. Review and adjust as needed.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left column — Client info */}
        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Company Info</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Company Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Spend Funding"
                    value={spendFunding}
                    onChange={(e) => setSpendFunding(e.target.value)}
                    fullWidth
                  >
                    {SPEND_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Target Launch Date"
                    type="date"
                    value={launchDate}
                    onChange={(e) => setLaunchDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>ZIP Codes</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Paste ZIP codes, one per line. Optionally include territory and vertical with pipes or tabs.
              </Typography>
              <TextField
                multiline
                minRows={6}
                maxRows={16}
                placeholder={`60601\n60614 | Downtown | Roofing\n60657 | North Side | Solar`}
                value={zipText}
                onChange={(e) => setZipText(e.target.value)}
                fullWidth
                variant="outlined"
              />
              {zipRows.length > 0 && (
                <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                  {zipRows.length} ZIP code{zipRows.length !== 1 ? 's' : ''} parsed
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right column — Verticals & Budget */}
        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Verticals</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {availableVerticals.map((v) => (
                  <Chip
                    key={v}
                    label={v}
                    onClick={() => handleToggleVertical(v)}
                    color={selectedVerticals.includes(v) ? 'primary' : 'default'}
                    variant={selectedVerticals.includes(v) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>

              {selectedVerticals.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>Monthly Budget per Vertical</Typography>
                  <Stack spacing={1.5}>
                    {selectedVerticals.map((v) => (
                      <TextField
                        key={v}
                        label={v}
                        type="number"
                        value={verticalBudgets[v] || ''}
                        onChange={(e) => setVerticalBudgets((prev) => ({ ...prev, [v]: e.target.value }))}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    ))}
                  </Stack>
                  <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#f5f5f5', textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Total Monthly Budget</Typography>
                    <Typography variant="h5" fontWeight={700}>{fmtCurrency(totalBudget)}</Typography>
                  </Paper>
                </>
              )}
            </CardContent>
          </Card>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubmit}
            disabled={!name.trim() || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            {isSubmitting ? 'Creating...' : 'Create Client'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
